import { useState } from 'react';
import styled from 'styled-components';
import { mapi } from './api';
import { Button, ButtonRow, Chip, Field, Grid2, Hint, Muted, Warn } from '../ui/primitives';
import type { BillingInfo, CustomerRow, FeatureMeta } from './types';
import { Osio } from './Osio';

/* Laskutustiedot ovat asiakkaan omassa tietokannassa: hän täyttää ne itse
   sovelluksensa Asetukset-välilehdellä. Master näyttää ne ja voi täydentää,
   jos tiedot on saatu muuta kautta — tieto pysyy silti yhdessä paikassa,
   ettei laskutusosoitteesta synny kahta eri versiota. */

const Ruudukko = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 6px 16px;
  margin: 8px 0;
`;

const Tieto = styled.div`
  font-size: 13.5px;
  .nimi { font-size: 11.5px; color: ${p => p.theme.color.muted}; text-transform: uppercase; letter-spacing: .03em; }
  .arvo { font-weight: 600; }
  .tyhja { color: ${p => p.theme.color.red}; font-weight: 600; }
`;

interface Props {
  asiakas: CustomerRow;
  kentat: FeatureMeta[];   // { key, label, hint } — sama muoto kuin ominaisuuksilla
  onToast: (v: string) => void;
  onSaved: (info: BillingInfo) => void;
}

export function BillingInfoCard({ asiakas, kentat, onToast, onSaved }: Props) {
  const info = asiakas.billingInfo;
  const [muokkaa, setMuokkaa] = useState(false);
  const [tulostaa, setTulostaa] = useState(false);
  // Muokkaustila käyttää omaa kopiota; katselutila lukee aina tuoreen propin,
  // jottei päivitetty data jää vanhan tilan taakse piiloon.
  const [arvot, setArvot] = useState<Record<string, string>>(info.arvot);

  const tallenna = async () => {
    try {
      const r = await mapi<{ billingInfo: BillingInfo }>(
        `/api/master/customers/${asiakas.id}/billing-info`, 'PUT', { values: arvot });
      onSaved(r.billingInfo);
      setMuokkaa(false);
      onToast(r.billingInfo.valmis
        ? 'Laskutustiedot tallennettu — valmis laskutettavaksi'
        : `Tallennettu, mutta puuttuu vielä: ${r.billingInfo.puuttuu.join(', ')}`);
    } catch (err) { onToast(err instanceof Error ? err.message : String(err)); }
  };

  const tulostaLasku = async () => {
    setTulostaa(true);
    try {
      const kk = new Date().toISOString().slice(0, 7);
      const res = await mapi<{ invoice: { id: number; number: string } }>(
        '/api/master/invoices', 'POST', { customer_id: asiakas.id, month: kk });
      window.open(`/api/master/invoices/${res.invoice.id}`, '_blank', 'noopener');
      onToast(`Lasku ${res.invoice.number} avattu. Sivulla on Tulosta / PDF.`);
    } catch (err) { onToast(err instanceof Error ? err.message : String(err)); }
    setTulostaa(false);
  };

  const kortti = info.arvot.maksutapa === 'kortti';
  const metodi = kortti
    ? (info.arvot.stripe_card_last4
      ? `Kortti (${info.arvot.stripe_card_brand || ''} •••• ${info.arvot.stripe_card_last4})`.replace(/\s+/g, ' ').trim()
      : 'Kortti — korttia ei ole vielä tallennettu')
    : (info.arvot.laskutus_verkkolasku
      ? `Lasku · verkkolasku ${info.arvot.laskutus_verkkolasku}`
      : (info.arvot.laskutus_email
        ? `Lasku · sähköposti ${info.arvot.laskutus_email}`
        : 'Lasku — toimitustapa puuttuu'));

  if (!info.saatavilla) {
    return <Warn>Laskutustietoja ei voitu lukea — asiakkaan tietokantaa ei löydy.</Warn>;
  }

  return (
    <Osio>
      <h4>
        Laskutustiedot
        {info.valmis
          ? <Chip $bg="#5C6B54">Valmis laskutettavaksi</Chip>
          : <Chip $bg="#A63D24">Puutteellinen</Chip>}
      </h4>

      {!muokkaa ? (
        <>
          <Ruudukko>
            {kentat.map(k => (
              <Tieto key={k.key}>
                <div className="nimi">{k.label}</div>
                {info.arvot[k.key]
                  ? <div className="arvo">{info.arvot[k.key]}</div>
                  : <div className="tyhja">— puuttuu</div>}
              </Tieto>
            ))}
          </Ruudukko>
          <Hint style={{ margin: '6px 0 0' }}>
            Maksutapa: <strong>{metodi}</strong>
          </Hint>
          {!info.valmis && (
            <Warn>
              Laskua ei kannata viedä kirjanpitoon ennen kuin puuttuu: {info.puuttuu.join(', ')}.
              Ensisijaisesti asiakas täyttää nämä itse sovelluksensa Asetukset-välilehdellä.
            </Warn>
          )}
          <ButtonRow style={{ margin: '6px 0 0' }}>
            <Button $small $variant="primary" disabled={tulostaa || !info.valmis} onClick={() => void tulostaLasku()}>
              {tulostaa ? 'Avataan…' : 'Tulosta lasku'}
            </Button>
            <Button $small onClick={() => { setArvot(info.arvot); setMuokkaa(true); }}>
              Täydennä tiedot
            </Button>
            <Muted style={{ fontSize: 12.5 }}>
              Asiakas täyttää nämä itse — täydennä vain jos tiedot on saatu muuta kautta.
            </Muted>
          </ButtonRow>
        </>
      ) : (
        <>
          <Hint style={{ marginTop: 0 }}>
            Tallennus kirjoittaa tiedot <strong>asiakkaan omaan tietokantaan</strong>, jolloin ne
            näkyvät myös hänelle itselleen. Näin laskutusosoitteesta ei synny kahta versiota.
          </Hint>
          <Grid2>
            {kentat.map(k => (
              <Field key={k.key}>
                <label htmlFor={`b_${asiakas.id}_${k.key}`}>{k.label}</label>
                <input
                  id={`b_${asiakas.id}_${k.key}`}
                  value={arvot[k.key] || ''}
                  placeholder={k.hint}
                  onChange={e => setArvot({ ...arvot, [k.key]: e.target.value })}
                />
              </Field>
            ))}
          </Grid2>
          <ButtonRow>
            <Button $variant="primary" onClick={tallenna}>Tallenna asiakkaan tietoihin</Button>
            <Button onClick={() => setMuokkaa(false)}>Peruuta</Button>
          </ButtonRow>
        </>
      )}
    </Osio>
  );
}
